<template>
  <section
    v-if="!loading"
    :class="sectionClass"
    class="block block-cards block--related"
  >
    <div class="container">
      <PageHierarchySection
        v-if="allStories.length"
        v-editable="blok || {}"
        :filtered-audiences="filteredAudiencesComputed"
        :filtered-topics="filteredTopicsComputed"
        :section="contentType"
        :subheading="subheading"
        :title="title"
        more-text="More like this"
      >
        <div class="row flex-wrap">
          <component
            :is="listType"
            :blok="allStories"
            :content-type="contentType"
            :is-filtered="allStories.length < maxQuantity"
          />
        </div>
      </PageHierarchySection>
    </div>
  </section>
</template>

<script setup lang="ts">
import shuffle from 'lodash/shuffle'
import type { QueryParams } from '~/modules/queryFormatters/DefaultQueryFormatter'
import { QueryFormatterFactory } from '~/modules/queryFormatters/QueryFormatterFactory'
import type {
  EventsStoryblok,
  FeaturedTopicPageStoryblok,
  NewsStoryblok,
  PageStoryblok,
  ResourceStoryblok,
  RealStoriesStoryblok,
} from '~/types/storyblok'

export type RelatedContent =
  | FeaturedTopicPageStoryblok
  | PageStoryblok
  | EventsStoryblok
  | ResourceStoryblok
  | NewsStoryblok
  | RealStoriesStoryblok

const props = withDefaults(
  defineProps<{
    blok: RelatedContent
    totalStories?: number
    filteredTopics?: string[]
    filteredAudiences?: string[]
    blockTitle?: string
    relatedContentTypes?: string[]
  }>(),
  {
    totalStories: 4,
    filteredTopics: () => [],
    filteredAudiences: () => [],
    blockTitle: 'You might also be interested in...',
    relatedContentTypes: () => [
      'resources',
      'news',
      'events',
      'featured-articles',
      'featured-topics',
      'audiences',
      'real-stories',
    ],
  },
)
const { sectionClass, pageId } = useStoryContent(toRefs(props))
const viewedStoriesStore = useViewedStoriesStore()
const { viewedStories } = storeToRefs(viewedStoriesStore)
const contentType = ref('related')
const loading = computed(() => status.value === 'pending')
const allStories = computed(() => [...relatedStories.value])
const title = computed(() => props.blok?.title || props.blockTitle)
const subheading = computed(() => props.blok?.subheading || '')
const listType = computed(() => contentType.value + 'List')
const maxQuantity = computed(() => props.blok?.quantity || props.totalStories)
const filteredTopicsComputed = computed(() => [props.filteredTopics].flat())
const filteredAudiencesComputed = computed(() =>
  props.filteredAudiences.filter((item) => item !== 'all'),
)
const queryFormatter = computed(() =>
  QueryFormatterFactory(slugToComponentTypeName(contentType.value)),
)
const sortBy = computed(() => queryFormatter.value.getDefaultSort())
const { fetchAsyncData } = useStoryblokHelpers()

const query = computed(() => ({
  filter_query: getRelatedStoriesFilterQuery(),
  by_slugs: getRelatedStoriesSlugs(),
  excluding_ids: pageId.value,
  // load more than we need, so we can randomise the order
  per_page: maxQuantity.value * 2,
  sort_by: sortBy.value,
}))

const { data, status } = await fetchAsyncData<unknown>('stories', query, {
  watch: [
    () => props.blok?.stories,
    filteredTopicsComputed,
    filteredAudiencesComputed,
  ],
})
const relatedStories = computed(
  () => shuffle(data.value.data.stories).slice(0, maxQuantity.value) || [],
)

function getRelatedStoriesFilterQuery() {
  // get a query designed for this type
  const options: QueryParams = {}
  if (filteredTopicsComputed.value.length) {
    options.topics = filteredTopicsComputed.value
  }
  if (filteredAudiencesComputed.value.length) {
    options.audiences = filteredAudiencesComputed.value
    options.ignoreAllOption = true
  }

  let filterQuery =
    queryFormatter.value.getFilterQuery(options)?.filter_query || {}

  // filter out any viewed items
  if (viewedStories.value.length) {
    filterQuery._uid = {
      not_in: [
        ...new Set([...viewedStories.value].map((item) => item.content._uid)),
      ].join(','),
    }
  }
  if (!Object.keys(filterQuery).length) {
    filterQuery = null
  }
  return filterQuery
}

function getRelatedStoriesSlugs() {
  return props.relatedContentTypes.map((item) => item + '/*').join(',')
}
</script>
